/**
 * The active interface locale, held at module scope so a pure helper or an
 * event handler can translate without a hook. `LocaleProvider` sets it before
 * its children render.
 */

/**
 * The catalogs that exist in `locales/`. `tutor_core::locale::SHIPPED` is the
 * server's copy, and `//client:catalog_contract_test` holds that the two and
 * the files are one set.
 */
// English first, then by code: the picker lists them in this order, and a
// code sorts the same whatever language the page is in.
export const SHIPPED_LOCALES = ['en', 'vi'] as const;

export type ShippedLocale = (typeof SHIPPED_LOCALES)[number];

export type Locale = ShippedLocale | 'qps';

/**
 * How each locale names itself, and which way it reads. The picker shows an
 * endonym in every interface language, never a translation of it, so a reader
 * stranded in a language they cannot read still finds their own.
 */
export const LOCALES: Readonly<Record<ShippedLocale, { endonym: string; dir: 'ltr' | 'rtl' }>> = {
  en: { endonym: 'English', dir: 'ltr' },
  // oxlint-disable-next-line tutor/no-english -- an endonym, the same in every locale
  vi: { endonym: 'Tiếng Việt', dir: 'ltr' },
};

export function isShipped(code: string): code is ShippedLocale {
  return (SHIPPED_LOCALES as readonly string[]).includes(code);
}

export function dirOf(locale: Locale): 'ltr' | 'rtl' {
  return locale === 'qps' ? 'ltr' : LOCALES[locale].dir;
}

/** `qps` is English underneath, so a screen reader reads it as English. */
export function langOf(locale: Locale): ShippedLocale {
  return locale === 'qps' ? 'en' : locale;
}

let active: Locale = 'en';

export function setLocale(locale: Locale): void {
  active = locale;
}

export function getLocale(): Locale {
  return active;
}
